import type { DecisionSection } from '@zerohuman/ui'
import type { GateRow } from '@/api'

/**
 * The one line a decision card leads with (#1760), worked out once for every surface that draws a gate (Needs you,
 * chat, the run page, Blockers). The card's reasoning then folds away under "Show details"; the lead is what the
 * person reads before deciding whether to open it.
 */

/** An action's lead: a line of what the run says it does, and the recommendation it wrote, when it wrote one. */
export type ActionLead = { line: string; recommendation: string | null }

/** A summary heading that carries the run's recommendation: "Recommendation", "Recommended action", … */
const RECOMMENDATION_HEADING = /^recommend/i

/**
 * An action card's lead, from the run's summary laid out under its headings: the first line of the first section,
 * and the first line under a "Recommendation" heading when there is one. Never an invented recommendation: a summary
 * with none leads with its line alone. Null when the summary has no line to lead with.
 */
export function actionLead(sections: readonly DecisionSection[]): ActionLead | null {
    const [first] = sections
    const line = first?.lines[0]
    if (!first || line === undefined) {
        return null
    }
    const recommended = sections.find(
        section => section !== first && RECOMMENDATION_HEADING.test(section.heading ?? ''),
    )
    return { line, recommendation: recommended?.lines[0] ?? null }
}

/** A heading over what a gate with choices suggests: a mail escalation's "Suggested action", a summary's "Recommendation…". */
const SUGGESTION_HEADING = /^(recommend|suggested action$)/i

/** The heading a mail escalation puts its summary under, and a stored summary may too. */
const SUMMARY_HEADING = /^summary$/i

/**
 * The lead of a gate that offers its own choices (a mail escalation's Reply / Snooze / Leave it), from what it decides
 * on (`gateDecisionSections`): the line under its "Summary" heading when it has one, else its first section's first
 * line, so a mail escalation leads with what the email is about rather than who sent it. The recommendation is the
 * first line under a "Suggested action" or "Recommendation…" heading, and never an invented one. Nothing folds on
 * such a card: what the choice is made on stays in view under the lead. Null when there is no line to lead with.
 */
export function choiceLead(sections: readonly DecisionSection[]): ActionLead | null {
    const from = sections.find(section => SUMMARY_HEADING.test(section.heading?.trim() ?? '')) ?? sections[0]
    const line = from?.lines[0]
    if (!from || line === undefined) {
        return null
    }
    const suggested = sections.find(
        section => section !== from && SUGGESTION_HEADING.test(section.heading?.trim() ?? ''),
    )
    return { line, recommendation: suggested?.lines[0] ?? null }
}

/**
 * Who a gate's run is asking on behalf of, as the enterprise names them: "Marcus (CMO)". Just the name or just the
 * role when only one is known, and null when neither is, so the wording then names nobody.
 */
export function askerLabel(gate: Pick<GateRow, 'askedBy'>): string | null {
    const name = gate.askedBy?.name?.trim() ?? ''
    const role = gate.askedBy?.role?.trim() ?? ''
    if (name && role) {
        return `${name} (${role})`
    }
    return name || role || null
}

/** "1 question", "2 questions". */
export function questionCount(count: number): string {
    return `${count} question${count === 1 ? '' : 's'}`
}

/**
 * A question card's lead. One that changes nothing says so first, in question words: nothing in the OS will change,
 * and the answer is only recorded. Otherwise the gate's own summary line when it wrote one, else who is asking and
 * how many questions.
 */
export function questionLead(input: {
    sections: readonly DecisionSection[]
    asker: string | null
    count: number
    changesNothing: boolean
}): string {
    const asked = input.asker ? `${input.asker} asks ${questionCount(input.count)}` : questionCount(input.count)
    if (input.changesNothing) {
        return `Nothing in the OS will change: ${asked}, and your answer is only recorded.`
    }
    const line = input.sections[0]?.lines[0]
    if (line !== undefined) {
        return line
    }
    return input.asker ? `${asked} of you.` : `${asked} for you.`
}
